import React from 'react';

interface ThinkingIndicatorProps {
  /** Optional status text e.g. "Searching documents..." */
  statusText?: string;
  size?: 'sm' | 'md';
}

/**
 * Smooth animated thinking dots with optional status text.
 * Three dots fade in sequence, similar to ChatGPT's typing indicator.
 */
export const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  statusText,
  size = 'md',
}) => {
  const dotSize = size === 'sm' ? 'w-1 h-1' : 'w-1.5 h-1.5';
  const gap = size === 'sm' ? 'gap-[3px]' : 'gap-1';

  return (
    <div className="flex items-center gap-2.5">
      <div className={`flex items-center ${gap}`} role="status" aria-label="Thinking">
        <span className={`${dotSize} rounded-full bg-primary-500/80 dark:bg-primary-400/80 thinking-dot`} style={{ animationDelay: '0ms' }} />
        <span className={`${dotSize} rounded-full bg-primary-500/80 dark:bg-primary-400/80 thinking-dot`} style={{ animationDelay: '160ms' }} />
        <span className={`${dotSize} rounded-full bg-primary-500/80 dark:bg-primary-400/80 thinking-dot`} style={{ animationDelay: '320ms' }} />
      </div>
      {statusText && (
        <span className={`${size === 'sm' ? 'text-[11px]' : 'text-xs'} text-medical-400 dark:text-medical-500 font-medium`}>
          {statusText}
        </span>
      )}

      <style>{`
        .thinking-dot {
          animation: thinkingFade 1.4s ease-in-out infinite;
        }
        @keyframes thinkingFade {
          0%, 100% { opacity: 0.25; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-2px); }
        }
      `}</style>
    </div>
  );
};
